import { Stack, Typography } from '@mui/material';
import z from 'zod';

import { ClearableSelect } from '@/components/management/formElements/ClearableSelect';
import { useAppForm } from '@/hooks/useAppForm';
import { useNavigationBlocker } from '@/hooks/useNavigationBlocker';
import { I18N_DE } from '@/i18n/de';
import type { CreateOrganizationDto, OrganizationType } from '@/modules/api/backend';
import { formScrollIntoViewOptions } from '@/utils/formScrollIntoViewOptions';

interface CreateOrganizationFormProps {
  setRef: (idx: number) => (ref: HTMLDivElement | null) => void;
  tabs: string[];
  onSubmit: (values: CreateOrganizationDto) => Promise<void>;
}

export function CreateOrganizationForm({ setRef, tabs, onSubmit }: CreateOrganizationFormProps) {
  const form = useAppForm({
    defaultValues: {
      name: '',
      organizationType: '',
      contact: '',
      url: '',
    },
    ...formScrollIntoViewOptions,
    onSubmit: async ({ value }) => {
      await onSubmit({
        ...value,
        organizationType: (value.organizationType as OrganizationType) || undefined,
      });
    },
  });

  useNavigationBlocker(form);

  return (
    <form
      id="organizationForm"
      onSubmit={(event) => {
        event.preventDefault();
        form.handleSubmit();
      }}
    >
      <Stack gap={2}>
        <Typography ref={setRef(0)} variant="h2">
          {tabs[0]}
        </Typography>

        <form.AppField
          name="name"
          validators={{
            onChange: z.string().nonempty(),
          }}
        >
          {(field) => <field.TextField label="Name" />}
        </form.AppField>

        <form.Field
          name="organizationType"
          validators={{
            onChange: z.string().nonempty(),
          }}
        >
          {(field) => (
            <ClearableSelect
              fullWidth
              error={!!field.state.meta.errors.length}
              helperText={field.state.meta.errors[0]?.message}
              label="Organisationstyp"
              onBlur={field.handleBlur}
              onChange={(event) => field.handleChange(event.target.value)}
              onClear={() => field.handleChange('')}
              sx={{ mb: 1 }}
              value={field.state.value ?? ''}
              options={Object.entries(I18N_DE.OrganizationType).map(([value, label]) => ({
                value,
                label,
              }))}
            />
          )}
        </form.Field>

        <form.AppField
          name="contact"
          validators={{
            onChange: z.string().nonempty(),
          }}
        >
          {(field) => <field.TextField label="Kontakt" />}
        </form.AppField>

        <form.AppField
          name="url"
          validators={{
            onChange: z.url(),
          }}
        >
          {(field) => <field.TextField label="Website" type="url" />}
        </form.AppField>
      </Stack>
    </form>
  );
}
